import { setBookingStartingPoint } from '@/helpers';
import { isDealsSearch } from '@/hooks/search/useSearch.helpers';
import { useReducer, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
  buildSuggestionSections,
  getSuggestionsFromSearchValue,
  removeSavedSuggestionValue,
  saveSuggestionValue,
} from './KeywordAutoSuggest.helpers';
import {
  KeywordAutoSuggestAction,
  KeywordAutoSuggestManagerProps,
  KeywordAutoSuggestState,
  KeywordSuggestion,
} from './KeywordAutoSuggest.type';

const SUGGESTION_TYPE_HISTORY = 'history';
const SUGGESTION_TYPE_MATCH = 'match';

const keywordAutoSuggestReducer = (
  state: KeywordAutoSuggestState,
  action: KeywordAutoSuggestAction,
): KeywordAutoSuggestState => {
  switch (action.type) {
    case 'ON_INPUT_CHANGE': {
      return { ...state, keyword: action.payload, isDirty: Boolean(action.payload) };
    }

    case 'ON_SUGGESTION_LOADED': {
      return { ...state, ...action.payload };
    }

    case 'SET_SHOW_SUGGESTIONS': {
      return {
        ...state,
        showSuggestions: action.payload,
        isDirty: action.payload ? false : state.isDirty,
      };
    }
    default:
      return state;
  }
};

const useKeywordAutoSuggestManager = ({
  initialState,
  searchType,
  onSelectKeywordSuggestion,
}: KeywordAutoSuggestManagerProps) => {
  const [state, dispatch] = useReducer(keywordAutoSuggestReducer, initialState);
  const history = useHistory();
  const abortControllerRef = useRef<AbortController | null>(null);
  const pendingValueRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);

  const handleOnChange = (_, { newValue: payload = '' }) => {
    dispatch({ type: 'ON_INPUT_CHANGE', payload });
  };

  const handleOnFocus = () => {
    dispatch({ type: 'SET_SHOW_SUGGESTIONS', payload: true });
  };

  const handleOnBlur = (_, { highlightedSuggestion }) => {
    /**
     * if the input is blurred, make sure to abort any ongoing fetch requests
     */
    abortControllerRef.current.abort();
  };

  const handleOnKeyDown = (event: React.KeyboardEvent) => {};

  const handleOnSuggestionsFetchRequested = async ({ value, reason }) => {
    if (reason !== 'input-focused' && reason !== 'input-changed') {
      return;
    }

    pendingValueRef.current = value;

    if (isFetchingRef.current) {
      return;
    }

    /**
     * When fetching keyword suggestions only one request is active at a time. If the user types during an ongoing request,
     * the latest input is saved in `pendingValueRef` and will be fetched after the current request finishes.
     *
     *  Suggestions are cached in localStorage to avoid sending duplicate requests for previously seen inputs.
     *
     * Example flow when user types "tractor":
     * - User types "tra" → triggers request #1
     * - Then types "trac" quickly → triggers request #2 (queued if #1 is still running)
     * - While request #2 is pending, user finishes typing "tractor" → input is saved (not immediately sent)
     * - Once request #2 finishes, request #3 is triggered with "tractor"
     * - Only the latest result ("tractor") is shown in the UI
     */
    const fetchSuggestions = async (input: string) => {
      isFetchingRef.current = true;

      if (abortControllerRef.current?.signal?.aborted || !abortControllerRef.current) {
        abortControllerRef.current = new AbortController();
      }

      const signal = abortControllerRef.current.signal;

      const { newServices, suggestions, storedSuggestions, hasHistory } = await getSuggestionsFromSearchValue(
        input,
        searchType,
        signal,
      );

      isFetchingRef.current = false;

      dispatch({
        type: 'ON_SUGGESTION_LOADED',
        payload: { suggestions, storedSuggestions, newServices, hasHistory },
      });

      /**
       * Check if there's a newer input value to process
       */
      if (
        pendingValueRef.current !== null &&
        pendingValueRef?.current !== input &&
        !abortControllerRef.current?.signal?.aborted
      ) {
        fetchSuggestions(pendingValueRef.current);
        pendingValueRef.current = null;
      }
    };

    fetchSuggestions(value);
  };

  const handleOnSuggestionsClearRequested = (props) => {
    abortControllerRef.current?.abort?.();
    dispatch({
      type: 'ON_SUGGESTION_LOADED',
      payload: { suggestions: [], storedSuggestions: [], newServices: [], hasHistory: false },
    });
  };

  const handleOnClearHistorySuggestion = (suggestion: KeywordSuggestion) => {
    if (suggestion.type !== 'history') return;

    removeSavedSuggestionValue(suggestion.text);

    const matches = state.storedSuggestions.filter((s) => s.type === SUGGESTION_TYPE_MATCH);
    const rest = state.storedSuggestions.filter(
      (s) => s.type !== SUGGESTION_TYPE_MATCH && s.type !== SUGGESTION_TYPE_HISTORY,
    );
    const currentHistory = state.storedSuggestions.find((s) => s.type === SUGGESTION_TYPE_HISTORY)?.suggestions ?? [];
    const historySuggestions = currentHistory.filter(
      (s) => !(s.type === SUGGESTION_TYPE_HISTORY && s.text === suggestion.text),
    );

    const { suggestions, storedSuggestions, hasHistory } = buildSuggestionSections({
      matches,
      rest,
      historySuggestions,
    });

    dispatch({
      type: 'ON_SUGGESTION_LOADED',
      payload: { suggestions, storedSuggestions, newServices: state.newServices, hasHistory },
    });
  };

  const handleOnSuggestionSelected = (event, { suggestion, suggestionValue }) => {
    if (event?.target?.id === 'remove_suggested') {
      return;
    }

    dispatch({ type: 'SET_SHOW_SUGGESTIONS', payload: false });

    const cleanedText = suggestionValue?.text?.replace('%', '') ?? suggestion.text ?? '';

    if (suggestion.type === 'place') {
      const { placeId, slug } = suggestion;

      let bookingStartingPoint = 'search_autocomplete';

      if (searchType === 'sistaminuten') {
        bookingStartingPoint = 'smt_search_autocomplete';
      }

      if (isDealsSearch(searchType)) {
        bookingStartingPoint = 'deals_search_autocomplete';
      }

      setBookingStartingPoint(bookingStartingPoint);

      if (isDealsSearch(searchType)) {
        const hasDeals = Boolean(suggestion.hasDeals);
        const hasSistaminuten = Boolean(suggestion.hasSistaminuten);
        onSelectKeywordSuggestion({ type: 'deal', keyword: cleanedText, placeId, hasDeals, hasSistaminuten });
        return;
      }

      let url = `/places/${slug}-${placeId}`;

      if (searchType === 'sistaminuten') {
        url += '?sistaminuten=1';
      }

      history.push(url);
      return;
    }

    if (suggestion.type === 'brand_deal') {
      onSelectKeywordSuggestion({ type: 'brand_deal', keyword: cleanedText, slug: suggestion.slug });
    } else {
      onSelectKeywordSuggestion({ type: suggestion.type, keyword: cleanedText });
    }
  };

  const saveKeywordToHistory = (keyword: string) => {
    const cleaned = keyword?.trim();
    if (!cleaned) return;
    saveSuggestionValue(cleaned);
  };

  const visibleSuggestions = state.isDirty
    ? state.suggestions.filter((s) => s.type !== SUGGESTION_TYPE_HISTORY)
    : state.suggestions;

  return {
    keyword: state.keyword ?? '',
    suggestions: visibleSuggestions,
    storedSuggestions: state.storedSuggestions,
    hasHistory: state.hasHistory,
    showSuggestions: state.showSuggestions,
    onChange: handleOnChange,
    onBlur: handleOnBlur,
    onFocus: handleOnFocus,
    onKeyDown: handleOnKeyDown,
    onSuggestionsFetchRequested: handleOnSuggestionsFetchRequested,
    onSuggestionsClearRequested: handleOnSuggestionsClearRequested,
    onSuggestionSelected: handleOnSuggestionSelected,
    onClearHistorySuggestion: handleOnClearHistorySuggestion,
    saveKeywordToHistory,
  };
};

export default useKeywordAutoSuggestManager;
